import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Account, AccountType, Money as MoneyModel } from 'api-client';
import fundsMovementIcon from 'assets/images/svg/transactions-sircle-filled.svg';
import { PlusIcon } from 'components/common/icons';
import { ComfortAccountTopupModal } from 'components/common/modals';
import { createAccountManager } from 'di';
import { useErrorCallback, useProfileData } from 'hooks';
import { cn, RouteDictionary } from 'utils';

import styles from '../AccountDetails.module.scss';
import { AccountDetailsBreadcrumbs } from '../shared/AccountDetailsBreadcrumbs';
import { AccountDetailsLoading } from '../shared/AccountDetailsLoading';
import { AccountHeaderCard } from '../shared/AccountHeaderCard';
import { AccountStatsCard } from '../shared/AccountStatsCard';
import { MainAccountInvestmentsTable } from '../shared/MainAccountInvestmentsTable';
import { useMainAccountInvestments } from '../shared/useMainAccountInvestments';
import { useMainAccountStats } from '../shared/useMainAccountStats';

const TITLE = 'Основной счет';

export const MainAccountDetails: FC = () => {
  const navigate = useNavigate();
  const profile = useProfileData();
  const handleError = useErrorCallback();
  const handleErrorRef = useRef(handleError);

  const [account, setAccount] = useState<Account | null>(null);
  const [comfortAccount, setComfortAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [topupModalOpen, setTopupModalOpen] = useState(false);
  const comfortAccountOpened = comfortAccount !== null;
  const stats = useMainAccountStats(account?.uuid);

  const {
    items: investments,
    loading: investmentsLoading,
    loadingMore,
    hasMore,
    statusFilter,
    changeStatusFilter,
    loadMore,
  } = useMainAccountInvestments(account?.uuid);

  useEffect(() => {
    handleErrorRef.current = handleError;
  }, [handleError]);

  const loadAccounts = useCallback(async () => {
    const accountManager = await createAccountManager();
    const [accountData, comfortAccountData] = await Promise.all([
      accountManager.getOne(AccountType.MAIN),
      accountManager.getOne(AccountType.COMFORT),
    ]);
    setAccount(accountData);
    setComfortAccount(comfortAccountData);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const accountManager = await createAccountManager();
        const [accountData, comfortAccountData] = await Promise.all([
          accountManager.getOne(AccountType.MAIN),
          accountManager.getOne(AccountType.COMFORT),
        ]);
        if (!cancelled) {
          setAccount(accountData);
          setComfortAccount(comfortAccountData);
        }
      } catch (error) {
        if (!cancelled && error instanceof Error) {
          await handleErrorRef.current(error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  const headerMoney = useMemo(
    () => account?.balance ?? new MoneyModel(0, profile.balance.currencyCode),
    [account, profile.balance.currencyCode]
  );

  if (loading) {
    return <AccountDetailsLoading />;
  }

  return (
    <div className={styles.container}>
      <AccountDetailsBreadcrumbs title={TITLE} />

      <AccountHeaderCard
        money={headerMoney}
        label="основной счет"
        actions={
          <>
            <button
              type="button"
              className={cn(styles.actionButton, !comfortAccountOpened && styles.actionButtonHalf)}
              onClick={() => navigate(`/dashboard/${RouteDictionary.DEPOSIT}`)}
            >
              <div className={styles.actionIcon}>
                <PlusIcon preset="circle-filled" size={40} />
              </div>
              <span className={styles.actionText}>
                Пополнить
                <br />
                основной
                <br />
                счет
              </span>
            </button>
            {comfortAccountOpened ? (
              <button
                type="button"
                className={styles.actionButton}
                onClick={() => setTopupModalOpen(true)}
                disabled={!account}
              >
                <div className={styles.actionIcon}>
                  <img src={fundsMovementIcon} alt="transfer" className={styles.transferIcon} />
                </div>
                <span className={styles.actionText}>
                  Перевести
                  <br />
                  на Комфорт
                  <br />
                  счет
                </span>
              </button>
            ) : null}
          </>
        }
      />

      <AccountStatsCard stats={stats} />

      <MainAccountInvestmentsTable
        investments={investments}
        loading={investmentsLoading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        statusFilter={statusFilter}
        onStatusFilterChange={changeStatusFilter}
        onLoadMore={loadMore}
        emptyMessage="Нет инвестиций"
      />

      {comfortAccountOpened ? (
        <ComfortAccountTopupModal
          active={topupModalOpen}
          onClose={() => setTopupModalOpen(false)}
          availableOnMainAccount={headerMoney}
          onSuccess={() => void loadAccounts()}
        />
      ) : null}
    </div>
  );
};
